// PRD §7.5 — Mentor engagement workspace.
// Sections per PRD: mission header, founder context, calls (intro + working,
// with recording playback once the watcher cron lands the Drive file),
// notes, progress updates (founder-submitted, mentor-reviewed), time logs,
// mark-complete. Mentors are monitors, not vendors — they REACT to founder
// progress, they don't ship work themselves.
import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { ArrowLeft, FileText, Clock, CheckCircle2, Plus, Video, PlayCircle, CalendarPlus } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";
import { RecordingNotice, HowMentorCallsWork } from "@/components/mentor-call-guide";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-mentor-token": localStorage.getItem("mentor_token") || "" });

export default function MentorEngagementDetail() {
  const [, params] = useRoute("/mentor/engagements/:id");
  const id = Number(params?.id);
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function refresh() {
    fetch(`${BASE}api/mentor-portal/engagements/${id}`, { headers: auth() })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(setData).catch(e => setErr(String(e)));
  }
  useEffect(() => { if (Number.isFinite(id)) refresh(); }, [id]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link href="/mentor/engagements" className="text-xs text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> All engagements</Link>
      <div className="text-sm text-red-400" data-testid="error-engagement">{err}</div>
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-10"><ProgressLoader active={true} title="Loading" stages={["Loading engagement"]} /></div>;

  const { engagement, mission, company, founder, notes, deliverables, timeLogs, calls = [] } = data;
  const totalHours = timeLogs.reduce((s: number, t: any) => s + Number(t.hours || 0), 0);

  async function postNote(body: string) {
    if (!body.trim()) return;
    setBusy("note");
    try {
      const r = await fetch(`${BASE}api/mentor-portal/engagements/${id}/notes`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  async function reviewProgress(dId: number, decision: string, feedback: string) {
    setBusy(`review-${dId}`);
    try {
      const r = await fetch(`${BASE}api/mentor-portal/engagements/${id}/deliverables/${dId}/review`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ decision, feedback }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  async function postTimeLog(hours: string, kind: string, note: string) {
    if (!hours) return;
    setBusy("time");
    try {
      const r = await fetch(`${BASE}api/mentor-portal/engagements/${id}/time-logs`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ hours: Number(hours), kind, note }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  async function scheduleCall(callId: number, startUtcIso: string, durationMin: number, agenda: string) {
    setBusy(`schedule-${callId}`);
    try {
      const r = await fetch(`${BASE}api/mentor-portal/intro-calls/${callId}/schedule`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ startUtcIso, durationMin, agenda: agenda || undefined }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  async function markComplete() {
    if (!confirm("Mark this mission complete? The founder will be asked to confirm.")) return;
    setBusy("complete");
    try {
      const r = await fetch(`${BASE}api/mentor-portal/engagements/${id}/mark-complete`, { method: "POST", headers: auth() });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `${r.status}`); }
      refresh();
    } catch (e: any) { alert(e.message); } finally { setBusy(null); }
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <Link href="/mentor/engagements" className="text-xs text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All engagements</Link>

      {/* Mission header */}
      <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-5" data-testid="mission-header">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-xl font-bold">{mission.title}</h1>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-300 whitespace-nowrap">{engagement.status}</span>
        </div>
        <div className="text-xs text-[var(--mentor-fg-muted)] mb-3">{company.name}{founder ? ` · ${founder.fullName}` : ""}{mission.deadline ? ` · due ${new Date(mission.deadline).toLocaleDateString()}` : ""}</div>
        <div className="text-sm text-[var(--mentor-fg)] mb-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mr-2">Problem</span>{mission.problemStatement}</div>
        <div className="text-sm text-[var(--mentor-fg)] mb-2"><span className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mr-2">Deliverable</span>{mission.deliverable}</div>
        {mission.whatGoodLooksLike && <div className="text-sm text-[var(--mentor-fg)]"><span className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mr-2">What good looks like</span>{mission.whatGoodLooksLike}</div>}
        {engagement.compMode && (
          <div className="mt-3 pt-3 border-t border-[var(--mentor-border)] text-xs text-[var(--mentor-fg-muted)]">
            Compensation: <span className="text-[var(--mentor-fg)]">{engagement.compMode}</span>{engagement.compTermsAgreedAt ? " · terms agreed" : " · terms pending"}
          </div>
        )}
      </div>

      {/* Founder context — derived from company description (no matching agent in pull-flow model) */}
      {company.description && (
        <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mb-2">About {company.name}</div>
          <p className="text-sm text-[var(--mentor-fg)] leading-relaxed">{company.description}</p>
        </div>
      )}

      {/* Calls (intro + working). Bot owns the Meet event; recording lands
          on googleMeetUrl when the watcher cron sees the Drive file. */}
      <Section icon={<Video className="w-3.5 h-3.5" />} title="Calls" testId="section-calls">
        <RecordingNotice scope="mentor" />
        <HowMentorCallsWork scope="mentor" />
        {busy?.startsWith("schedule-") ? (
          <ProgressLoader active={true} title="Scheduling call" stages={[
            "Reserving the slot",
            "Creating Google Meet event",
            "Sending calendar invites to you and the founder",
            "Logging recording-consent acknowledgement",
          ]} note="The Meet link and join button will appear here once Google confirms." />
        ) : (
          <CallsList calls={calls} side="mentor" busy={busy} onSchedule={scheduleCall} />
        )}
      </Section>

      {/* Progress updates — founder ships, mentor reviews. */}
      <Section icon={<FileText className="w-3.5 h-3.5" />} title="Progress updates" testId="section-deliverables">
        {deliverables.length === 0 ? (
          <Empty>No progress updates yet. When the founder logs work against this mission, you'll be able to react here.</Empty>
        ) : (
          <div className="divide-y divide-[var(--mentor-border)]">
            {deliverables.map((d: any) => (
              <ProgressReviewRow key={d.id} d={d} busy={busy === `review-${d.id}`}
                onReview={(dec, fb) => reviewProgress(d.id, dec, fb)} />
            ))}
          </div>
        )}
      </Section>

      {/* Notes */}
      <Section icon={<Plus className="w-3.5 h-3.5" />} title="Notes" testId="section-notes">
        <NoteForm onSubmit={postNote} disabled={busy === "note"} />
        {notes.length === 0 ? <Empty>No notes yet.</Empty> : (
          <div className="divide-y divide-[var(--mentor-border)] mt-3">
            {notes.map((n: any) => (
              <div key={n.id} className="py-2.5" data-testid={`note-${n.id}`}>
                <div className="text-[10px] text-[var(--mentor-fg-muted)] mb-1">{n.authorKind} · {new Date(n.createdAt).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Time logs */}
      <Section icon={<Clock className="w-3.5 h-3.5" />} title={`Time logged · ${totalHours.toFixed(2)}h`} testId="section-time">
        <TimeLogForm onSubmit={postTimeLog} disabled={busy === "time"} />
        {timeLogs.length === 0 ? <Empty>No time logged yet.</Empty> : (
          <div className="divide-y divide-[var(--mentor-border)] mt-3">
            {timeLogs.map((t: any) => (
              <div key={t.id} className="py-2 flex items-baseline justify-between gap-3" data-testid={`time-log-${t.id}`}>
                <div className="text-sm"><strong>{Number(t.hours).toFixed(2)}h</strong> <span className="text-xs text-[var(--mentor-fg-muted)]">· {t.kind}</span>{t.note && <span className="text-xs text-[var(--mentor-fg-muted)]"> — {t.note}</span>}</div>
                <div className="text-[10px] text-[var(--mentor-fg-muted)]">{new Date(t.createdAt).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Mark complete */}
      {engagement.status !== "completed" && engagement.status !== "completed_pending_founder" && (
        <button onClick={markComplete} disabled={busy === "complete"}
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-600/90 disabled:opacity-30 text-white text-sm font-semibold rounded-md px-5 py-2.5 transition"
          data-testid="button-mark-complete">
          <CheckCircle2 className="w-4 h-4" /> Mark mission complete
        </button>
      )}
      {engagement.status === "completed_pending_founder" && (
        <div className="text-center text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="banner-pending-founder">
          Awaiting founder confirmation to close out this engagement.
        </div>
      )}
      {engagement.status === "completed" && (
        <div className="text-center text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2" data-testid="banner-completed">
          Completed{engagement.completedAt ? ` on ${new Date(engagement.completedAt).toLocaleDateString()}` : ""}.
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, testId, children }: { icon: React.ReactNode; title: string; testId: string; children: React.ReactNode }) {
  return (
    <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)]">{icon}{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }: { children: React.ReactNode }) { return <div className="text-xs text-[var(--mentor-fg-muted)] italic">{children}</div>; }

function NoteForm({ onSubmit, disabled }: { onSubmit: (b: string) => void; disabled: boolean }) {
  const [v, setV] = useState("");
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(v); setV(""); }} className="flex flex-col gap-2 mb-2">
      <textarea value={v} onChange={e => setV(e.target.value)} rows={2} placeholder="Add a note…"
        className="w-full bg-black/30 border border-white/[0.12] rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50 resize-none"
        data-testid="input-note-body" />
      <button type="submit" disabled={disabled || !v.trim()} className="self-end bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-30 text-white text-xs font-semibold rounded px-3 py-1.5" data-testid="button-add-note">Add note</button>
    </form>
  );
}

// Mentor reads the founder's progress update and either approves it,
// asks for changes, or rejects it. Multiple reviews can stack — the latest
// one wins visually, but the trail stays for accountability.
function ProgressReviewRow({ d, busy, onReview }: { d: any; busy: boolean; onReview: (decision: string, feedback: string) => void }) {
  const [showReview, setShowReview] = useState(false);
  const [feedback, setFeedback] = useState("");
  const lastReview = d.reviews[d.reviews.length - 1];
  return (
    <div className="py-3" data-testid={`deliverable-${d.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-medium text-[var(--mentor-fg)]">{d.title}</div>
          {d.description && <div className="text-xs text-[var(--mentor-fg-muted)] mt-0.5">{d.description}</div>}
          {d.artifactUrl && <a href={d.artifactUrl} target="_blank" rel="noreferrer" className="text-xs text-[#FF5C00] hover:underline mt-1 inline-block">{d.artifactUrl}</a>}
        </div>
        <div className="text-[10px] text-[var(--mentor-fg-muted)] whitespace-nowrap">{new Date(d.submittedAt).toLocaleDateString()}</div>
      </div>
      {lastReview && (
        <div className={`mt-2 text-[11px] px-2 py-1.5 rounded inline-block ${lastReview.decision === "accepted" ? "bg-emerald-500/15 text-emerald-300" : lastReview.decision === "rejected" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
          You: <strong>{lastReview.decision.replace("_", " ")}</strong>{lastReview.feedback ? ` — ${lastReview.feedback}` : ""}
        </div>
      )}
      {!showReview ? (
        <button onClick={() => setShowReview(true)} className="mt-2 text-xs text-[#FF5C00] hover:underline" data-testid={`button-review-${d.id}`}>
          {lastReview ? "Add another review" : "Review"}
        </button>
      ) : busy ? (
        <div className="mt-2"><ProgressLoader active={true} title="Sending review" stages={[
          "Saving your decision",
          "Notifying the founder",
        ]} /></div>
      ) : (
        <div className="mt-2 space-y-2">
          <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={2} placeholder="Feedback (optional but recommended for changes/reject)"
            className="w-full bg-black/30 border border-[var(--mentor-border)] rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50 resize-none text-[var(--mentor-fg)]"
            data-testid={`input-feedback-${d.id}`} />
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => onReview("accepted", feedback)} className="bg-emerald-600 hover:bg-emerald-600/90 text-white text-xs font-semibold rounded px-3 py-1.5" data-testid={`button-accept-${d.id}`}>Approve</button>
            <button onClick={() => onReview("changes_requested", feedback)} className="bg-amber-600 hover:bg-amber-600/90 text-white text-xs font-semibold rounded px-3 py-1.5" data-testid={`button-changes-${d.id}`}>Request changes</button>
            <button onClick={() => onReview("rejected", feedback)} className="bg-red-600 hover:bg-red-600/90 text-white text-xs font-semibold rounded px-3 py-1.5" data-testid={`button-reject-${d.id}`}>Reject</button>
            <button onClick={() => { setShowReview(false); setFeedback(""); }} className="text-xs text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// CallsList — shared shape between mentor and founder side. The "side" prop
// only changes copy ("the founder" vs "the mentor"); both sides hit a
// /schedule endpoint that promotes pending_slot → scheduled. After a call
// runs, the recording-watcher cron overwrites googleMeetUrl with the Drive
// playback link, so the same field renders Join Meet OR Watch Recording
// depending on status.
function CallsList({ calls, side, busy, onSchedule }: {
  calls: any[]; side: "mentor" | "founder"; busy: string | null;
  onSchedule: (callId: number, startUtcIso: string, durationMin: number, agenda: string) => void;
}) {
  if (!calls || calls.length === 0) {
    return <Empty>No calls yet. When the founder accepts a mission you'll get an intro slot to schedule here.</Empty>;
  }
  const other = side === "mentor" ? "the founder" : "the mentor";
  return (
    <div className="divide-y divide-[var(--mentor-border)]">
      {calls.map((c: any) => {
        const isPending = c.status === "pending_slot";
        const isScheduled = c.status === "scheduled";
        const isCompleted = c.status === "completed";
        const isCancelled = c.status === "cancelled" || c.status === "no_show";
        const when = c.scheduledStartUtc ? new Date(c.scheduledStartUtc) : null;
        return (
          <div key={c.id} className="py-3" data-testid={`call-${c.id}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <div className="text-sm font-medium capitalize">{c.kind} call</div>
                {when && <div className="text-xs text-[var(--mentor-fg-muted)] mt-0.5">{when.toLocaleString()} · {c.durationMin || 45} min</div>}
                {!when && isPending && <div className="text-xs text-[var(--mentor-fg-muted)] mt-0.5">Slot not picked yet</div>}
              </div>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded whitespace-nowrap ${
                isCompleted ? "bg-emerald-500/15 text-emerald-300"
                : isScheduled ? "bg-sky-500/15 text-sky-300"
                : isCancelled ? "bg-zinc-500/15 text-zinc-400"
                : "bg-amber-500/15 text-amber-300"
              }`} data-testid={`call-status-${c.id}`}>{c.status.replace("_", " ")}</span>
            </div>
            {c.agenda && <div className="text-xs text-[var(--mentor-fg-muted)] mt-1 whitespace-pre-wrap">{c.agenda}</div>}
            {isScheduled && c.googleMeetUrl && (
              <a href={c.googleMeetUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#FF5C00] hover:underline"
                data-testid={`call-join-${c.id}`}>
                <Video className="w-3 h-3" /> Join Meet
              </a>
            )}
            {isCompleted && c.googleMeetUrl?.includes("drive.google.com") && (
              <a href={c.googleMeetUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#FF5C00] hover:underline"
                data-testid={`call-recording-${c.id}`}>
                <PlayCircle className="w-3 h-3" /> Watch recording
              </a>
            )}
            {isCompleted && !c.googleMeetUrl?.includes("drive.google.com") && (
              <div className="mt-2 text-[11px] text-[var(--mentor-fg-muted)] italic" data-testid={`call-no-recording-${c.id}`}>
                Recording not yet available. The watcher checks every 10 minutes after a call ends.
              </div>
            )}
            {isCancelled && (
              <div className="mt-2 text-[11px] text-[var(--mentor-fg-muted)]">
                Cancelled{c.cancelledByKind ? ` by ${c.cancelledByKind}` : ""}{c.cancellationReason ? ` — ${c.cancellationReason}` : ""}
              </div>
            )}
            {isPending && (
              <CallScheduleForm
                callId={c.id}
                defaultDuration={c.durationMin || 45}
                defaultAgenda={c.agenda || ""}
                disabled={busy === `schedule-${c.id}`}
                otherLabel={other}
                onSubmit={onSchedule}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CallScheduleForm({ callId, defaultDuration, defaultAgenda, disabled, otherLabel, onSubmit }: {
  callId: number; defaultDuration: number; defaultAgenda: string; disabled: boolean; otherLabel: string;
  onSubmit: (callId: number, startUtcIso: string, durationMin: number, agenda: string) => void;
}) {
  const [when, setWhen] = useState("");
  const [dur, setDur] = useState(String(defaultDuration));
  const [agenda, setAgenda] = useState(defaultAgenda);
  const [ack, setAck] = useState(false);
  const minLocal = (() => { const d = new Date(Date.now() + 15 * 60 * 1000); d.setSeconds(0, 0); return d.toISOString().slice(0, 16); })();
  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!when || !ack) return;
    const startUtcIso = new Date(when).toISOString();
    onSubmit(callId, startUtcIso, Number(dur) || 45, agenda.trim());
  }
  return (
    <form onSubmit={submit} className="mt-3 space-y-2 bg-black/20 border border-[var(--mentor-border)] rounded-md p-3" data-testid={`call-form-${callId}`}>
      <div className="text-[11px] text-[var(--mentor-fg-muted)]">Pick a slot. {otherLabel} will get a calendar invite + Meet link. Calls are recorded; the recording lands here when it ends.</div>
      <div className="flex flex-wrap gap-2">
        <input type="datetime-local" value={when} min={minLocal} onChange={e => setWhen(e.target.value)} required
          className="bg-black/30 border border-white/[0.12] rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50"
          data-testid={`input-call-when-${callId}`} />
        <select value={dur} onChange={e => setDur(e.target.value)}
          className="bg-black/30 border border-white/[0.12] rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50"
          data-testid={`select-call-duration-${callId}`}>
          <option value="30">30 min</option><option value="45">45 min</option><option value="60">60 min</option>
        </select>
      </div>
      <input value={agenda} onChange={e => setAgenda(e.target.value)} placeholder="Agenda (optional)"
        className="w-full bg-black/30 border border-white/[0.12] rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50"
        data-testid={`input-call-agenda-${callId}`} />
      <label className="flex items-start gap-2 text-[11px] text-[var(--mentor-fg-muted)] cursor-pointer">
        <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)}
          className="mt-0.5 accent-[#FF5C00]" data-testid={`check-consent-${callId}`} />
        <span>I understand this call will be recorded and made available in the engagement workspace, and I'll let {otherLabel} know.</span>
      </label>
      <button type="submit" disabled={disabled || !when || !ack}
        className="inline-flex items-center gap-1 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-30 text-white text-xs font-semibold rounded px-3 py-1.5"
        data-testid={`button-schedule-call-${callId}`}>
        <CalendarPlus className="w-3 h-3" /> {disabled ? "Scheduling…" : "Schedule call"}
      </button>
    </form>
  );
}

function TimeLogForm({ onSubmit, disabled }: { onSubmit: (h: string, k: string, n: string) => void; disabled: boolean }) {
  const [h, setH] = useState(""); const [k, setK] = useState("call"); const [n, setN] = useState("");
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(h, k, n); setH(""); setN(""); }} className="flex flex-wrap gap-2">
      <input value={h} onChange={e => setH(e.target.value)} type="number" step="0.25" min="0.25" max="24" placeholder="Hours"
        className="w-20 bg-black/30 border border-white/[0.12] rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50" data-testid="input-hours" />
      <select value={k} onChange={e => setK(e.target.value)}
        className="bg-black/30 border border-white/[0.12] rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50" data-testid="select-kind">
        <option value="call">call</option><option value="prep">prep</option><option value="async_review">async review</option>
      </select>
      <input value={n} onChange={e => setN(e.target.value)} placeholder="Note (optional)"
        className="flex-1 min-w-[120px] bg-black/30 border border-white/[0.12] rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#FF5C00]/50" data-testid="input-time-note" />
      <button type="submit" disabled={disabled || !h} className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-30 text-white text-xs font-semibold rounded px-3 py-1.5" data-testid="button-log-time">Log</button>
    </form>
  );
}
